import React, { useState, useEffect } from "react";
import { Search, Phone, Mail } from "lucide-react";
import { motion } from "framer-motion";
import "./partner-labs.css";
import { useNavigate } from "react-router";
import Loading from "../loading/Loading";

const PartnerLabs = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [labsPerPage, setLabsPerPage] = useState(6);
  const [pageInput, setPageInput] = useState("1");
  const [labs, setLabs] = useState([]);
  const [totalLabs, setTotalLabs] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState(null);
  const navigate = useNavigate();

  const API_ENDPOINT = "/wp-json/dbsb/v1/partners";

  useEffect(() => {
    const fetchLabs = async () => {
      setIsLoading(true);
      setApiError(null);

      try {
        if (!window.wpApiSettings?.nonce) {
          throw new Error("Authentication token not found. Please try logging in again.");
        }

        // Fetch all labs upfront with a high per_page value
        const response = await fetch(`${API_ENDPOINT}?per_page=100`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings.nonce,
          },
          credentials: "same-origin",
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || "Failed to fetch partner labs. Please try again.");
        }

        setLabs(data.data.map(lab => ({
          name: lab.name,
          addresses: [lab.address || ""],
          phone: lab.phone || "",
          email: lab.contact_email || "",
        })));
        setTotalLabs(data.total);
      } catch (error) {
        setApiError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLabs();
  }, []);

  const filteredLabs = labs.filter(
    (lab) =>
      lab.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lab.addresses.some((address) =>
        address.toLowerCase().includes(searchQuery.toLowerCase())
      )
  );

  const totalPages = Math.ceil(filteredLabs.length / labsPerPage);
  const startIndex = (currentPage - 1) * labsPerPage;
  const endIndex = Math.min(startIndex + labsPerPage, filteredLabs.length);
  const currentLabs = filteredLabs.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setPageInput(page.toString());
    }
  };

  const handlePageInputChange = (e) => {
    const value = e.target.value;
    setPageInput(value);
    const page = parseInt(value);
    if (!isNaN(page) && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleLabsPerPageChange = (e) => {
    setLabsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
    setPageInput("1");
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.5 } },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.1 } },
  };

  const searchVariants = {
    rest: {
      scale: 1,
      boxShadow: "0px 0px 0px rgba(0, 0, 0, 0)",
    },
    focus: {
      scale: 1.01,
      boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 20,
      },
    },
  };

  return (
    <motion.div
      className="partner-labs-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div className="partner-labs-card" variants={cardVariants}>
        {totalLabs === 0 && !isLoading ? null : (
          <>
            <motion.h4 className="card-subheading">
              View all partner labs
            </motion.h4>
            <motion.div
              className="labs-header"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <motion.div
                className="search-bar"
                variants={searchVariants}
                initial="rest"
                whileFocus="focus"
                whileHover="focus"
              >
                <Search size={20} color="#656E71" />
                <input
                  type="text"
                  placeholder="Search labs by name or address"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </motion.div>
              <div className="contact-section">
                <span>Can't find your city?</span>
                <motion.button
                  onClick={() => navigate("/contact-form")}
                  className="contact-us"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Contact us
                </motion.button>
              </div>
            </motion.div>
          </>
        )}
        {isLoading ? (
          <motion.div
            className="no-labs"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Loading />
          </motion.div>
        ) : apiError ? (
          <motion.div
            className="no-labs"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              {apiError}
            </motion.p>
          </motion.div>
        ) : totalLabs > 0 ? (
          <>
            <motion.div
              className="labs-list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              {currentLabs.map((lab, index) => (
                <motion.div
                  key={index}
                  className="lab-box"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * index }}
                >
                  <h3>{lab.name}</h3>
                  {lab.addresses.map((address, addrIndex) => (
                    <p key={addrIndex}>{address}</p>
                  ))}
                  {lab.phone && (
                    <div className="lab-contact-row">
                      <Phone size={16} style={{ marginRight: 6, verticalAlign: "middle" }} />
                      <span>{lab.phone}</span>
                    </div>
                  )}
                  {lab.email && (
                    <div className="lab-contact-row">
                      <Mail size={16} style={{ marginRight: 6, verticalAlign: "middle" }} />
                      <span>{lab.email}</span>
                    </div>
                  )}
                </motion.div>
              ))}
            </motion.div>
            <div className="pagination-card-container">
              <motion.div
                className="pagination"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <div className="page-selector">
                  <motion.input
                    type="text"
                    value={pageInput}
                    onChange={handlePageInputChange}
                    className={
                      parseInt(pageInput) > totalPages ? "invalid" : ""
                    }
                    whileFocus={{
                      scale: 1.05,
                      boxShadow: "0px 0px 5px rgba(58, 63, 106, 0.2)",
                    }}
                  />
                  <span>of {totalPages} pages</span>
                </div>
                <div className="page-arrows">
                  <motion.button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    whileHover={currentPage !== 1 ? { scale: 1.1 } : {}}
                    whileTap={currentPage !== 1 ? { scale: 0.9 } : {}}
                  >
                    <svg
                      className="arrow-button-icon"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                  </motion.button>
                  <motion.button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    whileHover={
                      currentPage !== totalPages ? { scale: 1.1 } : {}
                    }
                    whileTap={currentPage !== totalPages ? { scale: 0.9 } : {}}
                  >
                    <svg
                      className="arrow-button-icon"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="9 6 15 12 9 18"></polyline>
                    </svg>
                  </motion.button>
                </div>
                <div className="labs-per-page">
                  <span>Labs per page:</span>
                  <div className="select-wrapper">
                    <motion.select
                      value={labsPerPage}
                      onChange={handleLabsPerPageChange}
                      whileFocus={{ scale: 1.05 }}
                      whileHover={{ scale: 1.05 }}
                    >
                      <option value={6}>6</option>
                      <option value={7}>7</option>
                      <option value={8}>8</option>
                      <option value={9}>9</option>
                      <option value={10}>10</option>
                    </motion.select>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                </div>
              </motion.div>
              <span className="pagination-page-info">
                {startIndex + 1}-{endIndex}
                <span>/{filteredLabs.length} labs</span>
              </span>
            </div>
          </>
        ) : (
          <motion.div
            className="no-labs"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <motion.img
              src={`${process.env.PUBLIC_URL}/assets/no-tests.png`}
              alt="No partner labs"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            />
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              No partner labs
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              All partner labs will appear here.
            </motion.p>
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default PartnerLabs;